import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import NavItemModal from "./NavItemModal";
import type { MenuNavigationItem } from "./types";

export interface Props {
  item: MenuNavigationItem;
  rulesBarText?: string;
  isOpen: boolean;
  onToggle: () => void;
  onOpen?: () => void;
  onClose?: () => void;
}

const CLOSE_DELAY_MS = 100;

function NavItem({ item, rulesBarText, isOpen, onToggle, onOpen, onClose }: Props) {
  const [isHovered, setIsHovered] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  const cancelScheduledClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const scheduleClose = () => {
    cancelScheduledClose();
    closeTimer.current = setTimeout(() => {
      onClose?.();
    }, CLOSE_DELAY_MS);
  };

  const shouldShowModal = !!item?.categories?.length || !!item?.description ||
    !!item?.images?.length;
  const hoverColor = item.highlight
    ? (item.highlightColor || "#000000")
    : "#333333";

  // Portal pro document.body: o backdrop precisa ficar FORA da árvore DOM do
  // <li>/wrapper com onMouseLeave, senão (por ser fixed + cobrir a tela
  // inteira) o ponteiro nunca "sai" do wrapper de verdade — o mouseleave
  // nunca dispara e o submenu só fecha no clique. Guard de `document` evita
  // erro de SSR (o backdrop só existe depois de uma interação client-side).
  const backdrop = shouldShowModal && isOpen && typeof document !== "undefined"
    ? createPortal(
      <div
        className="fixed inset-0 opacity-50 z-40 bg-[#333333]"
        onClick={onToggle}
      />,
      document.body,
    )
    : null;

  return (
    <li
      className="group flex items-center border-b-[3px] item-menu hover:font-bold"
      style={{
        // Com submenu, a marcação segue `isOpen`: `isHovered` pode ficar preso
        // em true (o backdrop em portal remove o mouseleave do <li>) e deixaria
        // a borda marcada depois que o submenu fechou.
        borderBottomColor: (shouldShowModal ? isOpen : isHovered)
          ? hoverColor
          : "#FFFFFF",
      }}
      onMouseEnter={() => {
        setIsHovered(true);
        if (shouldShowModal) {
          cancelScheduledClose();
          onOpen?.();
        }
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        if (shouldShowModal) scheduleClose();
      }}
    >
      <div className="navitem-name-and-arrow flex flex-col items-center h-[30px] justify-end mb-4">
        {shouldShowModal
          ? (
            <a
              href={item.url}
              className="flex"
              aria-expanded={isOpen}
              onClick={(e) => {
                if (!isOpen) {
                  e.preventDefault();
                  onOpen?.();
                }
              }}
            >
              <span
                className={`font-['Poppins'] not-italic font-normal lg:text-sm 2xl:text-[15px] leading-[18px] 2xl:tracking-[0.06em] whitespace-nowrap uppercase item-name ${
                  item.highlight
                    ? "!font-medium text-[#000000] item-name-highlight"
                    : "text-[#333333]"
                }`}
                style={item.highlightColor
                  ? { color: item.highlightColor }
                  : undefined}
              >
                {item.name}
              </span>
            </a>
          )
          : (
            <a className="flex" href={item.url}>
              <span
                className={`font-['Poppins'] not-italic font-normal lg:text-sm 2xl:text-[15px] leading-[18px] 2xl:tracking-[0.06em] whitespace-nowrap uppercase item-name ${
                  item.highlight
                    ? "!font-medium text-[#000000] item-name-highlight"
                    : "text-[#333333]"
                }`}
                style={item.highlightColor
                  ? { color: item.highlightColor }
                  : undefined}
              >
                {item.name}
              </span>
            </a>
          )}
      </div>

      <div
        className={`fixed inset-0 z-50 flex flex-col justify-start items-start transition-opacity duration-100 ease-linear max-h-[350px] top-60 ${
          isOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
        onMouseEnter={cancelScheduledClose}
        onMouseLeave={() => {
          if (shouldShowModal) scheduleClose();
        }}
      >
        {shouldShowModal && isOpen && (
          <NavItemModal
            navItem={item}
            rulesBarText={rulesBarText}
          />
        )}
      </div>
      {backdrop}
    </li>
  );
}

export default NavItem;
